import React, { useEffect } from "react"
import { useField, useFormikContext } from "formik"
import { ConfigQuery, DimensionDetailsForUserResponse, fetchDimensionDetailsForUser } from "../../createOrderApi"
import { handleAutoCompleteMode, useAutocompleteController } from "./useAutocompleteController"
import { ApiQueryAutocomplete } from "../../../../../core/api/components/ApiQueryAutocomplete"
import { SelectedMainUserState } from "../../../createOrder/createOrderTypes"
import { ExtendedDynamicConfigKey, DynamicConfigField } from "./formTypes"
import { getFormikFieldError, isAccountingDimensionKey } from "./helpers"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useFormResetWatcher } from "./useFormResetWatcher"

function transformData(data: DimensionDetailsForUserResponse) {
    return data.accountDimension.costCenters.map((item) => ({
        id: item.value,
        name: item.name,
    }))
}

type DynamicAutocompleteProps = {
    config: DynamicConfigField
    user?: SelectedMainUserState
}

export function DynamicAutocomplete({ config, user }: DynamicAutocompleteProps) {
    if (!user) return <></>

    const [field, meta, helpers] = useField(config.key)
    const { submitCount } = useFormikContext<any>()
    const formikErrorProps = getFormikFieldError(meta, submitCount)
    const wasReset = useFormResetWatcher(config.key)

    const mode = handleAutoCompleteMode(config.inputField?.mode)

    const {
        debouncedSearch,
        enabled,
        isEagerLoad,
        isLoadOnOpen,
        forceRefreshKey,
        handleInputChange,
        handleOpen,
        handleClose,
        handleClearSearch,
    } = useAutocompleteController(mode, wasReset)

    function getAutocompleteQueryConfig(key: ExtendedDynamicConfigKey, user: SelectedMainUserState, search: string) {
        if (isAccountingDimensionKey(key)) {
            const dimensionId = Number(key.split("_")[2])
            return {
                fetchFn: fetchDimensionDetailsForUser,
                queryName: ConfigQuery.ACCOUNT_DIMENSION_DETAILS_FOR_USER,
                dependencies: {
                    userId: user.userId,
                    dimensionId: config?.dimensionId || dimensionId,
                    search,
                },
                select: transformData,
            }
        }

        return null
    }

    const queryConfig = getAutocompleteQueryConfig(config.key, user, debouncedSearch)
    if (!queryConfig) return <></>

    const {
        data: options = [],
        isError,
        isLoading,
    } = useApiQuery<any, any, any>(queryConfig.fetchFn as any, {
        queryName: queryConfig.queryName,
        dependencies: {
            ...queryConfig.dependencies,
            forceRefreshKey,
        },
        select: queryConfig.select,
        enabled,
    })

    useEffect(() => {
        if (isEagerLoad && options.length === 1 && !field.value) {
            helpers.setValue(options[0])
        }
    }, [isEagerLoad, options, field.value, helpers])

    const handleChange = (_event: React.SyntheticEvent, value: any) => {
        helpers.setValue(value)
        if (isLoadOnOpen) {
            handleClearSearch()
        }
    }

    return (
        <ApiQueryAutocomplete
            isLoading={enabled && isLoading}
            isError={isError}
            options={options}
            getOptionLabel={(option) => option.name || ""}
            value={field.value || null}
            onChange={handleChange}
            onInputChange={handleInputChange}
            onOpen={handleOpen}
            onClose={handleClose}
            size="small"
            slotProps={{
                textField: formikErrorProps,
            }}
            label={`${config?.label || config?.description}${config?.inputField?.isRequired ? " *" : ""}`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(x) => x}
            readOnly={isEagerLoad && options.length === 1}
        />
    )
}
